import { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
// import { Link } from 'react-router-dom';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Button from '@material-ui/core/Button';
import IconButton from '../../Buttons/IconButtonTooltip';

import ReloadIcon from '@material-ui/icons/Replay';
import AddIcon from '@material-ui/icons/Add';
import WarningIcon from '@material-ui/icons/Warning';

import { useAuthContext } from '../../../contexts/AuthContext';
// import { usePopupWindow } from '../../../helpers/hooks';
import {
    getUsers,
    getUserStatus,
    deleteUser as serviceDeleteUser,
    resetUser as serviceResetUser,
    resetAllUsers as serviceResetAllUsers,
    registerTestUser,
} from '../../../services/Master';
import { changeArray } from '../../../helpers/state';
import ConfirmationDialog from '../../Dialog/Confirmation';
import TestUserPopup from './TestUserPopup';
import UserItem, { columns } from './UserItem';

const ButtonContainer = styled.div`
    display: flex;
    margin-top: 32px;
`;
const StyledButton = styled((props) => (
    <Button variant="contained" {...props} />
))`
    margin-right: 8px;
`;

const UserList = ({ gameToken }) => {
    const { auth } = useAuthContext();
    const [userList, setUserList] = useState([]);
    const [confirmationDialog, setConfimationDialog] = useState(null);
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const [showTestUserPopup, setShowTestUserPopup] = useState(false);
    const [busy, setBusy] = useState(false);
    // const openPopup = usePopupWindow();
    // const openRegisterWindow = () => {
    //     openPopup(getRegisterUrl(gameToken), {
    //         width: 600,
    //         height: 400,
    //     })
    // };

    const reloadUsers = () => {
        getUsers(auth.token, gameToken)
            .then((data) => {
                setUserList(data);
            })
            .catch((e) => {
                console.error('Cannot find users', e);
            });
    };

    useEffect(() => {
        let mounted = true;
        if (auth.token && gameToken) {
            getUsers(auth.token, gameToken)
                .then((data) => {
                    if (mounted) {
                        setUserList(data);
                    }
                })
                .catch((e) => {
                    console.error('Cannot find users', e);
                });
        }

        return () => {
            mounted = false;
        };
    }, [auth.token, gameToken]);

    const addUserStatus = useCallback(
        (userToken) => {
            const getParameter = (obj, key, alternative) => {
                const isset = (val) =>
                    !(typeof val === 'undefined' || val === null);
                return isset(obj?.[key]) ? obj[key] : alternative;
            };
            getUserStatus(auth.token, userToken).then((status) => {
                setUserList((prevList) => {
                    return changeArray(
                        prevList,
                        (user) => user.userToken === userToken,
                        (user) => ({
                            ...user,
                            status: getParameter(status, 'status', '-'),
                            step:
                                getParameter(status, 'step', false) === false &&
                                getParameter(status, 'steps', false) === false
                                    ? '-'
                                    : `${getParameter(
                                          status,
                                          'step',
                                          0
                                      )}/${getParameter(status, 'steps', '?')}`,
                            attempts:
                                getParameter(status, 'attempts', false) ===
                                    false &&
                                getParameter(status, 'maxAttempts', false) ===
                                    false
                                    ? '-'
                                    : `${getParameter(
                                          status,
                                          'attempts',
                                          0
                                      )}/${getParameter(
                                          status,
                                          'maxAttempts',
                                          '?'
                                      )}`,
                        })
                    );
                });
            });
        },
        [auth.token]
    );

    useEffect(() => {
        let mounted = true;
        if (mounted && userList && userList.length) {
            const nextUser = userList.find((user) => !user.status);
            if (nextUser) {
                addUserStatus(nextUser.userToken);
            }
        }

        return () => {
            mounted = false;
        };
    }, [userList, addUserStatus]);

    const closeConfirmation = () => {
        setConfimationDialog((p) => ({ ...p, open: false }));
    };

    const confirmAction = (message, onAccept, onCancel = closeConfirmation) => {
        setConfimationDialog({
            children: message,
            onAccept: () => {
                closeConfirmation();
                onAccept();
            },
            onCancel,
            open: true,
            title: t('dialog.Warning'),
        });
    };

    const resetUser = (userToken) => {
        let msg = t('dialog.resetUserText', { returnObjects: true });
        if (Array.isArray(msg)) {
            msg = msg.map((line, i) => <p key={i}>{line}</p>);
        }
        confirmAction(msg, () =>
            serviceResetUser(auth.token, userToken).then(() => {
                reloadUsers();
            })
        );
    };
    const deleteUser = (userToken) => {
        let msg = t('dialog.deleteUserText', { returnObjects: true });
        if (Array.isArray(msg)) {
            msg = msg.map((line, i) => <p key={i}>{line}</p>);
        }
        confirmAction(msg, () =>
            serviceDeleteUser(auth.token, userToken).then(() => {
                reloadUsers();
            })
        );
    };

    const resetAllUsers = () => {
        let msg = t('dialog.resetAllUsersText', { returnObjects: true });
        if (Array.isArray(msg)) {
            msg = msg.map((line, i) => <p key={i}>{line}</p>);
        }
        confirmAction(msg, () => {
            setBusy(true);
            // Promise.all(
            //     userList.map((user) =>
            //         serviceResetUser(auth.token, user.userToken)
            //     )
            // )
            serviceResetAllUsers(auth.token, gameToken)
                .then(() => {
                    reloadUsers();
                })
                .catch((err) => {
                    enqueueSnackbar(`${err}`, {
                        variant: 'error',
                    });
                })
                .finally(() => {
                    setBusy(false);
                });
        });
    };

    const handleRegisterTestUser = (name) => {
        registerTestUser(auth.token, gameToken, name)
            .then(() => {
                reloadUsers();
            })
            .catch((err) => {
                enqueueSnackbar(`${t('error.registerTestUser')} (${err})`, {
                    variant: 'error',
                });
            });
        setShowTestUserPopup(false);
    };

    return (
        <div>
            <TableContainer style={{ maxWidth: '100%' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {columns.map((col) => {
                                return (
                                    <TableCell key={col.key}>
                                        {col.label}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {userList.map((user) => {
                            return (
                                <UserItem
                                    key={user.userToken}
                                    user={user}
                                    addUserStatus={() =>
                                        addUserStatus(user.userToken)
                                    }
                                    deleteUser={() =>
                                        deleteUser(user.userToken)
                                    }
                                    resetUser={() => resetUser(user.userToken)}
                                    disabled={busy}
                                />
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <ButtonContainer>
                <StyledButton
                    onClick={() => setShowTestUserPopup(true)}
                    startIcon={<AddIcon />}
                    disabled={busy}
                >
                    {t('buttons.registerTestUser')}
                </StyledButton>
                <StyledButton
                    onClick={resetAllUsers}
                    startIcon={<WarningIcon />}
                    disabled={busy}
                >
                    {t('buttons.resetAllUsers')}
                </StyledButton>
                {/* <Link
                    component={StyledButton}
                    to={`/student/register?game=${gameToken}`}
                    target="_blank"
                >
                    {t('buttons.registerUser')}
                </Link> */}
                <IconButton
                    aria-label="reload"
                    onClick={reloadUsers}
                    disabled={busy}
                    tooltip="reload user data"
                >
                    <ReloadIcon />
                </IconButton>
            </ButtonContainer>
            {confirmationDialog && (
                <ConfirmationDialog
                    {...confirmationDialog}
                ></ConfirmationDialog>
            )}
            {showTestUserPopup && (
                <TestUserPopup
                    open={showTestUserPopup}
                    cancel={() => {
                        setShowTestUserPopup(false);
                    }}
                    submit={(name) => {
                        handleRegisterTestUser(name);
                    }}
                />
            )}
        </div>
    );
};

export default UserList;
